import { ref, computed } from 'vue'
import { useThrottleFn } from '@vueuse/core'
import { useWaitingNewsStore } from '@/ContextTab/stores/newsSet/task-panel/waiting'
import { useInProgressNewsStore } from '@/ContextTab/stores/newsSet/task-panel/inProgress'
import { useMyNewsStore } from '@/ContextTab/stores/newsSet/task-panel/my'
import type { TaskPanelNewsSetType } from '@/lib/types'


type TaskPanelStore =
  ReturnType<typeof useMyNewsStore>
  | ReturnType<typeof useWaitingNewsStore>
  | ReturnType<typeof useInProgressNewsStore>

const DEFAULT_SELECTED_ID = -1

export function useTaskPanelList(setType: TaskPanelNewsSetType) {

  let store: TaskPanelStore | null = null

  if (setType === 'waiting') {
    store = useWaitingNewsStore()
  } else if (setType === 'inProgress') {
    store = useInProgressNewsStore()
  } else if (setType === 'my') {
    store = useMyNewsStore()
  }

  if (!store) {
    throw Error(`Incorrect task panel setType requested: ${setType}`)
  }

  const items = computed(() => store.newsList)
  const hasMoreItems = computed(() => store.hasMoreItems)
  const isFetching = computed(() => store.isFetching)

  const initialLoaded = computed(() =>
    !isFetching.value && (items.value.length || !hasMoreItems.value),
  )

  const errors = computed(() => {
    return store.error ? [store.error] : []
  })

  const selectedId = ref(DEFAULT_SELECTED_ID)

  function selectItem(id?: number) {
    selectedId.value = id ?? DEFAULT_SELECTED_ID
  }

  const onReachedEnd = useThrottleFn(async () => {
    if (store?.hasMoreItems) {
      await store?.loadNext()
    }
  }, 500, false, true)

  async function reload() {
    await store?.reload()
  }

  // first loadNext not needed, as we do it in hooks/authWatcher :: loadTaskPanel()

  return {
    items,
    reload,
    selectedId,
    selectItem,
    onReachedEnd,
    hasMoreItems,
    errors,
    isFetching,
    initialLoaded,
  }
}
